import { Paragraph, TableRow, TableCell, VerticalAlign, WidthType, Table } from 'docx';
import { tableBorders, TextStyle } from '../reportThemes';
import { EsrsReportMetric } from '../../Report.types';

export const B3HardcodedMetrics = [
  'vsmeEnergyConsumptionFromElectricity',
  'vsmeEnergyConsumptionFromElectricityNonRenewable',
  'vsmeEnergyConsumptionFromFuels',
  'vsmeEnergyConsumptionFromFuelsNonRenewable',
];

const getMetricValue = (metrics: EsrsReportMetric[], ref: string): number | '' => {
  const metric = metrics.find((m) => m.metric.metricRef === ref);
  if (metric?.tableData?.result?.isAnswerNotPresent) {
    return '';
  }
  const value = metric?.tableData?.result?.Year;
  return value === undefined || value === null ? '' : Number(value);
};

const getUnitValue = (metrics: EsrsReportMetric[], ref: string) => {
  return metrics.find((m) => m.metric.metricRef === ref)?.metric.metric.unitOfMeasurement;
};

const getTotal = (renewableValue: number | '', nonRenewableValue: number | '') => {
  if (
    (renewableValue === '' || renewableValue === undefined) &&
    (nonRenewableValue === '' || nonRenewableValue === undefined)
  )
    return '';
  return (Number(renewableValue) || 0) + (Number(nonRenewableValue) || 0);
};

const getEnergyConsumptionRows = (metrics: EsrsReportMetric[]) => {
  const electricityRenewable = getMetricValue(metrics, 'vsmeEnergyConsumptionFromElectricity');
  const electricityNonRenewable = getMetricValue(
    metrics,
    'vsmeEnergyConsumptionFromElectricityNonRenewable'
  );
  const fuelsRenewable = getMetricValue(metrics, 'vsmeEnergyConsumptionFromFuels');
  const fuelsNonRenewable = getMetricValue(metrics, 'vsmeEnergyConsumptionFromFuelsNonRenewable');

  return [
    {
      label: 'Electricity (as reflected in utility billings)',
      renewable: electricityRenewable,
      nonRenewable: electricityNonRenewable,
      total: getTotal(electricityRenewable, electricityNonRenewable),
    },
    {
      label: 'Fuels',
      renewable: fuelsRenewable,
      nonRenewable: fuelsNonRenewable,
      total: getTotal(fuelsRenewable, fuelsNonRenewable),
    },
  ];
};

export const createEnergyConsumptionMixTableRows = (metrics: EsrsReportMetric[]) => {
  const rows = getEnergyConsumptionRows(metrics);

  return rows.map(
    (row) =>
      new TableRow({
        children: [
          new TableCell({
            verticalAlign: VerticalAlign.TOP,
            children: [new Paragraph({ text: row.label, style: TextStyle.body })],
          }),
          new TableCell({
            verticalAlign: VerticalAlign.TOP,
            children: [new Paragraph({ text: String(row.renewable), style: TextStyle.body })],
          }),
          new TableCell({
            verticalAlign: VerticalAlign.TOP,
            children: [new Paragraph({ text: String(row.nonRenewable), style: TextStyle.body })],
          }),
          new TableCell({
            verticalAlign: VerticalAlign.TOP,
            children: [new Paragraph({ text: String(row.total), style: TextStyle.body })],
          }),
        ],
      })
  );
};

export const createB3Table = (metrics: EsrsReportMetric[]) => {
  const tableContentRows = createEnergyConsumptionMixTableRows(metrics);
  const unitOfMeasurementRenewable = getUnitValue(metrics, 'vsmeEnergyConsumptionFromElectricity');
  const unitOfMeasurementNonRenewable = getUnitValue(
    metrics,
    'vsmeEnergyConsumptionFromElectricityNonRenewable'
  );

  const headerRow = new TableRow({
    tableHeader: true,
    children: [
      new TableCell({
        verticalAlign: VerticalAlign.CENTER,
        width: { size: '25%', type: WidthType.PERCENTAGE },
        children: [new Paragraph({ text: '', style: TextStyle.tableTitle })],
      }),
      new TableCell({
        verticalAlign: VerticalAlign.CENTER,
        width: { size: '25%', type: WidthType.PERCENTAGE },
        children: [
          new Paragraph({
            text: `Renewable Energy Consumption (${unitOfMeasurementRenewable})`,
            style: TextStyle.tableTitle,
          }),
        ],
      }),
      new TableCell({
        verticalAlign: VerticalAlign.CENTER,
        width: { size: '25%', type: WidthType.PERCENTAGE },
        children: [
          new Paragraph({
            text: `Non-renewable Energy Consumption (${unitOfMeasurementNonRenewable})`,
            style: TextStyle.tableTitle,
          }),
        ],
      }),
      new TableCell({
        verticalAlign: VerticalAlign.CENTER,
        width: { size: '25%', type: WidthType.PERCENTAGE },
        children: [
          new Paragraph({
            text: `Total Energy Consumption (${unitOfMeasurementRenewable})`,
            style: TextStyle.tableTitle,
          }),
        ],
      }),
    ],
  });

  return new Table({
    columnWidths: [8000, 2000],
    margins: { top: 60, bottom: 60, right: 60, left: 60 },
    width: { size: '100%', type: WidthType.PERCENTAGE },
    borders: tableBorders,
    rows: [headerRow, ...tableContentRows],
  });
};
